import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";

const contactMethods = ["Email", "Phone", "Video Call"];
const stages = ["Idea/Concept", "Start-Up (0–2 years)", "Established (2+ years)", "Scaling/Growth Phase"];
const serviceOptions = ["Strategic Branding", "Digital Marketing", "Entertainment & Media"];

const inputCls = "w-full bg-transparent border-b border-[#1F222E] rounded-none focus:border-[#D4AF37] focus:outline-none px-0 py-3 text-white placeholder:text-[#6B7280] transition-colors";
const labelCls = "text-xs text-[#9CA3AF] uppercase tracking-wider mb-2 block";

export default function InquiryForm() {
  const [form, setForm] = useState({
    full_name: "", business_name: "", email: "", phone: "",
    preferred_contact: "Email", industry: "", business_stage: "Idea/Concept",
    services: [], message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
      toast.success("Inquiry received. We'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-[#1F222E] bg-[#12141A] p-12 text-center" data-testid="inquiry-success">
        <div className="h-14 w-14 mx-auto grid place-items-center border border-[#D4AF37] text-[#D4AF37] rounded-full">
          <Check size={26} />
        </div>
        <h3 className="font-display text-2xl mt-6 text-white">Thank you, {form.full_name.split(" ")[0]}.</h3>
        <p className="text-[#9CA3AF] mt-3 max-w-md mx-auto">Your inquiry has been received. Our team will reach out via {form.preferred_contact.toLowerCase()} to schedule your free consultation.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-[#1F222E] bg-[#12141A] p-8 md:p-12 space-y-8" data-testid="inquiry-form">
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <label className={labelCls}>Full Legal Name *</label>
          <input className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jane Doe" data-testid="inquiry-full-name" />
        </div>
        <div>
          <label className={labelCls}>Business / Brand Name</label>
          <input className={inputCls} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Optional" data-testid="inquiry-business-name" />
        </div>
        <div>
          <label className={labelCls}>Email Address *</label>
          <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" data-testid="inquiry-email" />
        </div>
        <div>
          <label className={labelCls}>Phone Number</label>
          <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Optional" data-testid="inquiry-phone" />
        </div>
        <div>
          <label className={labelCls}>Industry / Sector</label>
          <input className={inputCls} value={form.industry} onChange={(e) => set("industry", e.target.value)} placeholder="e.g. Fashion, SaaS, Music" data-testid="inquiry-industry" />
        </div>
        <div>
          <label className={labelCls}>Preferred Contact Method</label>
          <div className="flex gap-3 pt-2">
            {contactMethods.map((m) => (
              <button type="button" key={m} onClick={() => set("preferred_contact", m)}
                className={`px-4 py-2 text-sm border rounded-sm transition-colors ${form.preferred_contact === m ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#1F222E] text-[#9CA3AF] hover:border-[#3a3e4c]"}`}
                data-testid={`inquiry-contact-${m.toLowerCase().replace(/\s/g, "-")}`}>{m}</button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className={labelCls}>Current Business Stage</label>
        <div className="flex flex-wrap gap-3 pt-1">
          {stages.map((s) => (
            <button type="button" key={s} onClick={() => set("business_stage", s)}
              className={`px-4 py-2 text-sm border rounded-sm transition-colors ${form.business_stage === s ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#1F222E] text-[#9CA3AF] hover:border-[#3a3e4c]"}`}
              data-testid={`inquiry-stage-${s.split(/[\s/]/)[0].toLowerCase()}`}>{s}</button>
          ))}
        </div>
      </div>

      <div>
        <label className={labelCls}>Services of Interest (check all that apply)</label>
        <div className="grid sm:grid-cols-3 gap-3 pt-1">
          {serviceOptions.map((s) => {
            const active = form.services.includes(s);
            return (
              <button type="button" key={s} onClick={() => toggleService(s)}
                className={`flex items-center gap-3 px-4 py-3 text-sm border rounded-sm text-left transition-colors ${active ? "border-[#D4AF37] text-white" : "border-[#1F222E] text-[#9CA3AF] hover:border-[#3a3e4c]"}`}
                data-testid={`inquiry-service-${s.split(" ")[0].toLowerCase()}`}>
                <span className={`h-4 w-4 grid place-items-center border ${active ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#3a3e4c]"}`}>
                  {active && <Check size={12} className="text-[#0B0C10]" />}
                </span>
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className={labelCls}>Tell us about your vision</label>
        <textarea rows={3} className={inputCls + " resize-none"} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Where are you today and where do you want to go?" data-testid="inquiry-message" />
      </div>

      <button type="submit" disabled={submitting}
        className="bg-[#D4AF37] text-[#0B0C10] font-medium hover:bg-[#F1D570] transition-colors rounded-sm px-10 py-4 disabled:opacity-60"
        data-testid="inquiry-submit">
        {submitting ? "Submitting…" : "Book My Free Consultation"}
      </button>
    </form>
  );
}
